import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiSliders, FiCode, FiDownload, FiMail, FiCheck, FiUsers, FiEye, FiTrash2 } from 'react-icons/fi';
import { toast } from '../../Toast';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import { HubHeader, HubTip, SearchBox, FilterChips, SelectBox, IconButton, Pager, Pill, StatCard, hubPath, useHubSite } from '../shared';
import { MESSAGES, MessageStatus, ContactMessageItem, STATUS_TONE, statusCounts, initials } from './contactData';
import ExportMessagesModal from './ExportMessagesModal';
import DeleteMessageModal from './DeleteMessageModal';

type Filter = 'all' | MessageStatus;
const FORMS = ['All forms', 'Contact us', 'Investor enquiry'];

// Website Hub › Contact forms (spec 408:9939)
const ContactForms: React.FC = () => {
    const history = useHistory();
    const { host } = useHubSite();
    const [rows, setRows] = useState(MESSAGES);
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState<Filter>('all');
    const [form, setForm] = useState(FORMS[0]);
    const [exporting, setExporting] = useState(false);
    const [deleting, setDeleting] = useState<ContactMessageItem | null>(null);

    const counts = statusCounts(rows);
    const needle = q.trim().toLowerCase();
    // "All" leaves spam out, as the spam chip's count is separate
    const shown = rows.filter(m =>
        (filter === 'all' ? m.status !== 'Spam' : m.status === filter) &&
        (form === FORMS[0] || m.form === form) &&
        [m.name, m.email, m.subject, m.body].some(s => s.toLowerCase().includes(needle)));
    const total = filter === 'all' ? counts.All : counts[filter];

    const chips: { value: Filter; label: string; count: number }[] = [
        { value: 'all', label: 'All', count: counts.All },
        { value: 'New', label: 'New', count: counts.New },
        { value: 'Replied', label: 'Replied', count: counts.Replied },
        { value: 'Archived', label: 'Archived', count: counts.Archived },
        { value: 'Spam', label: 'Spam', count: counts.Spam },
    ];

    const open = (m: ContactMessageItem) => history.push(hubPath('contact-forms', 'message', m.id));

    return <>
        <HubHeader title="Contact Forms" sub="Messages sent through the Contact us and Investor enquiry forms on the website">
            <button type="button" className="btn-outline with-icon" data-tour="hub-contact-form-settings" onClick={() => history.push(hubPath('contact-forms', 'settings'))}><FiSliders /> Form Settings</button>
            <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/widgets/contact-form')}><FiCode /> Widget Settings</button>
            <button type="button" className="btn-outline with-icon" onClick={() => setExporting(true)}><FiDownload /> Export</button>
        </HubHeader>

        <div className="hub-stats">
            <StatCard icon={<FiMail />} label="New messages" value={counts.New} sub="Waiting for a reply" badge={counts.New > 0 ? <span className="badge warn">Action needed</span> : undefined} />
            <StatCard icon={<FiCheck />} label="Replied this month" value="14" sub="Average reply time 6 hours" />
            <StatCard icon={<FiUsers />} label="From registered investors" value={`8 of ${counts.All}`} sub="Investi matched them to sign‑ups" />
            <StatCard icon={<ShieldCheckIcon />} label="Spam blocked" value={counts.Spam} sub="Automatically filtered this month" />
        </div>

        <section className="card hub-list">
            <div className="hub-toolbar">
                <SearchBox value={q} onChange={setQ} placeholder="Search name, email or message" />
                <FilterChips chips={chips} value={filter} onChange={setFilter} />
                <SelectBox label="Form" value={form} options={FORMS} onChange={setForm} className="hub-form-select" />
            </div>

            <div className="hub-table hub-msg-table">
                <div className="tr th"><span className="hub-c-from">From</span><span className="hub-c-msg">Message</span><span className="hub-c-form">Form</span><span className="hub-c-recv">Received</span><span className="status">Status</span><span className="act" /></div>
                {/* the tour's step 1 highlights the newest message */}
                {shown.map((m, i) => <div key={m.id} className="tr clickable" data-tour={i === 0 ? 'hub-contact-messages' : undefined} onClick={() => open(m)}>
                    <span className="hub-c-from">
                        <span className="av">{initials(m.name)}</span>
                        <span className="t"><strong>{m.name}</strong><span>{m.email}</span></span>
                    </span>
                    <span className="hub-c-msg">{m.subject}</span>
                    <span className="hub-c-form">{m.form}</span>
                    <span className="hub-c-recv">{m.received}</span>
                    <span className="status"><Pill tone={STATUS_TONE[m.status]}>{m.status}</Pill></span>
                    <span className="act">
                        <IconButton label="Read message" onClick={() => open(m)}><FiEye /></IconButton>
                        <IconButton label="Delete" danger onClick={() => setDeleting(m)}><FiTrash2 /></IconButton>
                    </span>
                </div>)}
                {shown.length === 0 && <p className="hub-empty">No messages match — try another filter.</p>}
            </div>

            <Pager shown={shown.length} total={total} noun="messages" />
        </section>

        <HubTip>New messages are also emailed to info@{host}. Replying here keeps the whole conversation in one place for your team.</HubTip>

        {exporting && <ExportMessagesModal rows={shown} viewCount={total} allRows={rows} onClose={() => setExporting(false)} />}
        {deleting && <DeleteMessageModal name={deleting.name} subject={deleting.subject} onClose={() => setDeleting(null)}
            onArchive={() => { setRows(xs => xs.map(x => x.id === deleting.id ? { ...x, status: 'Archived' } : x)); setDeleting(null); toast.info('Preview only: archived here, nothing was saved'); }}
            onDelete={() => { setRows(xs => xs.filter(x => x.id !== deleting.id)); setDeleting(null); toast.info('Preview only: deleted here, nothing was saved'); }} />}
    </>;
};

export default ContactForms;
